import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { adminGet, adminPut, adminDelete, formatApiError } from "../lib/api";

export const STATUS_OPTIONS = [
  { v: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { v: "contacted", label: "Contacted", color: "bg-yellow-100 text-yellow-800" },
  { v: "won", label: "Won", color: "bg-green-100 text-green-700" },
  { v: "lost", label: "Lost", color: "bg-gray-200 text-gray-700" },
];

const matchesSearch = (lead, q) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return ["name", "email", "company", "project_type", "message"].some((k) =>
    (lead[k] || "").toLowerCase().includes(needle)
  );
};

const matchesStatus = (lead, statusFilter) =>
  statusFilter === "all" || (lead.status || "new") === statusFilter;

/**
 * Encapsulates list loading, status updates, deletion, filtering, and selection
 * for the admin Leads page.
 */
export const useLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLeads(await adminGet("/leads"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = useCallback(
    async (id, status) => {
      try {
        await adminPut(`/leads/${id}`, { status });
        setLeads((s) => s.map((l) => (l.id === id ? { ...l, status } : l)));
        setActive((a) => (a?.id === id ? { ...a, status } : a));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    },
    []
  );

  const remove = useCallback(async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await adminDelete(`/leads/${id}`);
      setLeads((s) => s.filter((l) => l.id !== id));
      setActive((a) => (a?.id === id ? null : a));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const filtered = useMemo(
    () => leads.filter((l) => matchesStatus(l, statusFilter) && matchesSearch(l, filter)),
    [leads, statusFilter, filter]
  );

  return {
    leads,
    filtered,
    loading,
    active,
    setActive,
    filter,
    setFilter,
    statusFilter,
    setStatusFilter,
    changeStatus,
    remove,
  };
};
